<template lang="pug">
  .b-supplier-request-item(data-testid="supplier-item" :class="{ highlighted }")
    ._head
      span._photo(data-testid="supplier-item-photo")
        img._image(v-if="photoUrl" :src="photoUrl" :alt="row.item.name")
      ._fields
        span._name(data-testid="supplier-item-name") {{ row.item.name }}
        ._props
          span._prop(data-testid="supplier-item-brand") {{ row.item.brand || '' }}
          span._prop(data-testid="supplier-item-series") {{ row.item.series || '' }}
          span._prop(data-testid="supplier-item-article") {{ row.item.article || '' }}
          span._prop(data-testid="supplier-item-color") {{ row.item.colorMaterial || '' }}
          span._prop(data-testid="supplier-item-dimensions") {{ row.item.dimensions || '' }}
        ._props
          span._prop
            | запрошено:
            = ' '
            span(data-testid="supplier-item-quantity") {{ quantity(row.item.quantity) }}
            = ' '
            span(data-testid="supplier-item-unit") {{ unitTitle(row.item.unit) }}
          span._prop
            | требуемый срок:
            = ' '
            span(data-testid="supplier-item-delivery") {{ deliveryText(row.deliveryTerm, 'не задан') }}
          span._prop(v-if="row.plannedPrice !== null && row.plannedPrice !== undefined")
            | плановая цена:
            = ' '
            span(data-testid="supplier-item-planned-price") {{ money(row.plannedPrice) }}
      ._marks
        //- Маркер перехода «к позиции» из события чата или из уведомления (ТЗ 6.10, 6.8): есть в
        //- разметке только у той позиции, к которой привёл переход
        span._mark.pointed(v-if="highlighted" data-testid="supplier-item-highlighted")
          | вы перешли к этой позиции
        span._mark.rework(v-if="row.isRework" data-testid="supplier-item-rework") на доработке
        span._mark.attention(
          v-if="row.confirmationRequired"
          data-testid="supplier-item-confirmation"
          ) требуется подтверждение
        span._mark(v-if="row.reRequestedAt" data-testid="supplier-item-rerequested")
          | запрошено повторно {{ date(row.reRequestedAt) }}
    ._rework-comment(v-if="row.isRework && row.reworkComment" data-testid="supplier-item-rework-comment")
      | Комментарий дизайнера: {{ row.reworkComment }}

    ._split(v-if="row.chosenSplit")
      span._split-value
        | Цена для заказчика за единицу:
        = ' '
        span(data-testid="supplier-item-customer-price") {{ money(row.chosenSplit.customerPrice) }}
      span._split-value
        | по позиции:
        = ' '
        span(data-testid="supplier-item-customer-total") {{ money(row.chosenSplit.customerTotal) }}
      span._split-value
        | Вознаграждение дизайнера за единицу:
        = ' '
        span(data-testid="supplier-item-designer-reward") {{ money(row.chosenSplit.designerReward) }}
      span._split-value
        | по позиции:
        = ' '
        span(data-testid="supplier-item-designer-reward-total") {{ money(row.chosenSplit.designerRewardTotal) }}

    ._offers
      ._offer(v-for="offer in row.offers" :key="offer.id" data-testid="supplier-offer")
        ._offer-line
          //- выбор дизайнера окончателен только после утверждения заказчиком (ТЗ 2.5)
          span._state(data-testid="supplier-offer-state") {{ offer.state === 'chosen' ? 'Выбран дизайнером' : offerStateTitle(offer.state) }}
          span._changed(v-if="offer.changedAt" data-testid="supplier-offer-changed")
            | изменён {{ dateTime(offer.changedAt) }}
          span._value(data-testid="supplier-offer-quantity") {{ quantity(offer.quantity) }} {{ unitTitle(row.item.unit) }}
          span._value(data-testid="supplier-offer-price") {{ money(offer.supplierPrice) }}
          span._value(data-testid="supplier-offer-discount") {{ discountText(offer) }}
          span._value(data-testid="supplier-offer-total") = {{ money(offer.total) }}
          span._value(data-testid="supplier-offer-delivery") {{ deliveryText(offer.deliveryTerm, '—') }}
          span._value(data-testid="supplier-offer-replacement")
            | {{ offer.isReplacement ? (offer.replacementText || 'замена') : '' }}
          span._value(data-testid="supplier-offer-comment") {{ offer.comment || '' }}
        ._blocked(v-if="isBlocked(offer)" data-testid="supplier-offer-blocked")
          | отправить нельзя: позиция у заказчика или в закупке
        ._offer-actions
          Btn(v-if="canEdit(offer)" grey xs data-testid="supplier-offer-edit" @click="$emit('edit', offer)") Изменить
          Btn(
            v-if="canDelete(offer)"
            grey
            xs
            data-testid="supplier-offer-delete"
            @click="$emit('delete', offer)"
            ) Удалить
          Btn(
            v-if="canWithdraw(offer)"
            grey
            xs
            data-testid="supplier-offer-withdraw"
            @click="$emit('withdraw', offer)"
            ) Отозвать
          Btn(
            v-if="canConfirm(offer)"
            green
            xs
            data-testid="supplier-offer-confirm"
            @click="$emit('confirm', offer)"
            ) Подтвердить
          label._replace(v-if="canConfirm(offer)")
            | Загрузить новое КП вместо подтверждения
            input._file(
              type="file"
              data-testid="supplier-offer-replace-input"
              @change="onReplaceFile"
              )
          Btn(
            v-if="offer.state !== 'draft'"
            grey
            xs
            data-testid="supplier-offer-history"
            @click="$emit('history', offer)"
            ) История
        ._history(v-if="historyOfferId === offer.id")
          ._history-empty(v-if="!history.length") Записей пока нет
          ._history-record(
            v-for="record in history"
            :key="record.id"
            data-testid="supplier-offer-history-record"
            )
            span._time {{ dateTime(record.createdAt) }}
            span._field {{ fieldTitle(record.field) || historyKind(record) }}
            span._values {{ record.oldValue || '—' }} → {{ record.newValue || '—' }}
            span._reason(v-if="record.reason") {{ record.reason }}

        SupplierOfferForm(
          v-if="editingOfferId === offer.id"
          :offer="offer"
          :requestedQuantity="row.item.quantity"
          :sent="offer.state !== 'draft'"
          @save="$emit('save', { offer, params: $event })"
          @cancel="$emit('cancel')"
          )

    SupplierOfferForm(
      v-if="addingForm"
      :requestedQuantity="row.item.quantity"
      @save="$emit('save', { offer: null, params: $event })"
      @cancel="$emit('cancel')"
      )

    ._add(v-if="canAddOffer")
      Btn(blue xs data-testid="supplier-item-add-offer" @click="$emit('add')") + Отклик вручную
</template>

<script>
import SupplierOfferForm from '@/components/supplier/SupplierOfferForm.vue';
import {
  money, quantity, unitTitle, deliveryText, date, dateTime, offerStateTitle, percent, fieldTitle,
  HISTORY_KINDS,
} from '@/utils/spec';

/**
 * Позиция запроса в кабинете поставщика с её откликами (ТЗ 6.5): обезличенные поля позиции из среза
 * поставщика (3.5), метки «на доработке» с комментарием, «запрошено повторно» и «требуется
 * подтверждение», цена для заказчика и вознаграждение дизайнера после разделения скидки (за единицу
 * и по позиции, ТЗ 3.4), черновики и отправленные отклики с доступными действиями.
 *
 * Какие действия доступны, решает сервер: признаки «отзыв возможен», «изменение возможно»,
 * «требуется подтверждение» и «отклики принимаются» приходят в ответе (ТЗ 4.2); недоступные
 * действия не показываются (6.9). Компонент только показывает и сообщает наружу.
 * Сгенерировано Claude.
 */
export default {
  name: 'SupplierRequestItem',
  components: { SupplierOfferForm },
  props: {
    /** Позиция запроса из карточки: item, deliveryTerm, plannedPrice, признаки, offers. */
    row: { type: Object, required: true },
    /**
     * Готовый признак «черновики можно править»: его считает страница по карточке запроса (архив и
     * статус «Завершён», ТЗ 6.5, 2.7) — здесь он только используется, чтобы одно и то же правило не
     * считалось в двух местах.
     */
    writable: Boolean,
    /** Проект в архиве: все операции записи недоступны, в том числе отзыв и подтверждение (ТЗ 3.5). */
    readOnly: Boolean,
    /** Переход «к позиции» из события чата или из уведомления: позиция подсвечена (ТЗ 6.10, 6.8). */
    highlighted: Boolean,
    /** Какой отклик правится и открыта ли форма нового отклика по этой позиции. */
    editingOfferId: Number,
    addingForm: Boolean,
    historyOfferId: Number,
    history: { type: Array, default: () => [] },
  },
  computed: {
    photoUrl() {
      const photo = this.row.item?.photo;
      return photo?.thumbnails?.small ?? photo?.url ?? null;
    },
    canAddOffer() {
      return this.writable && !this.addingForm;
    },
  },
  methods: {
    money,
    quantity,
    unitTitle,
    deliveryText,
    date,
    dateTime,
    offerStateTitle,
    fieldTitle,
    discountText(offer) {
      const value = offer.discountType === 'percent'
        ? percent(offer.discountValue)
        : money(offer.discountValue);
      return `${value} (${money(offer.discount, '—')})`;
    },
    historyKind(record) {
      return HISTORY_KINDS[record.kind] ?? '';
    },
    /** Черновик по позиции, ушедшей заказчику или в закупку (ТЗ 6.5, 2.2 «отклики принимаются»). */
    isBlocked(offer) {
      return offer.state === 'draft' && this.row.offersAccepted === false;
    },
    canEdit(offer) {
      return this.writable && (offer.state === 'draft' || offer.canEdit);
    },
    canDelete(offer) {
      return this.writable && offer.state === 'draft';
    },
    canWithdraw(offer) {
      return !this.readOnly && !!offer.canWithdraw;
    },
    /** «Подтвердить» и замена новым КП — при признаке «требуется подтверждение» (ТЗ 6.5, 2.8). */
    canConfirm(offer) {
      return !this.readOnly && offer.state === 'chosen' && !!this.row.confirmationRequired;
    },
    onReplaceFile(event) {
      // ссылка на само поле: сбрасываем его сразу, иначе повторный выбор того же файла не даст change
      const input = event.target;
      const [file] = Array.from(input.files ?? []);
      input.value = '';
      if (file) this.$emit('replace-quote', file);
    },
  },
};
</script>

<style scoped lang="scss">
.supplier-request-item {
  padding: 12px 14px;
  border: 1px solid var(--color-super-light);
  border-radius: 8px;
  background: var(--color-white);

  // позиция, к которой привёл переход из события чата или из уведомления (ТЗ 6.10, 6.8)
  &.highlighted {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.15);
  }

  &__head {
    display: flex;
    align-items: flex-start;
    gap: 14px;
  }

  &__photo {
    display: block;
    flex: 0 0 56px;
    width: 56px;
    height: 56px;
    border-radius: 6px;
    background: #F1F5FA;
    overflow: hidden;
  }

  &__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__fields {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__name {
    font-weight: 700;
    font-size: 15px;
  }

  &__props {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__marks {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }

  &__mark {
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 10px;
    background: #F1F5FA;
    color: var(--color-middle-gray);

    &.rework { color: var(--color-orange); }
    &.attention { color: var(--color-red); }
    &.pointed { color: var(--color-accent); }
  }

  &__rework-comment {
    margin-top: 8px;
    font-size: 12px;
    color: var(--color-orange);
  }

  &__split {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 20px;
    font-size: 13px;
  }

  &__offers {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__offer {
    padding: 8px 10px;
    border-radius: 6px;
    background: #F8FAFD;
  }

  &__offer-line {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 14px;
    font-size: 13px;
  }

  &__state {
    font-weight: 700;
  }

  &__changed {
    font-size: 12px;
    color: var(--color-orange);
  }

  &__blocked {
    margin-top: 4px;
    font-size: 12px;
    color: var(--color-red);
  }

  &__offer-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
  }

  &__replace {
    font-size: 12px;
    color: var(--color-accent);
    cursor: pointer;
  }

  &__file {
    position: absolute;
    opacity: 0;
    visibility: hidden;
  }

  &__history {
    margin-top: 6px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__history-record {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 12px;
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__history-empty {
    font-size: 12px;
    color: var(--color-middle-gray);
  }

  &__add {
    margin-top: 10px;
  }

  // Узкие экраны (общий порог модуля — 768 px): фото, поля и метки встают в колонку,
  // действия отклика переносятся по строкам
  @media (max-width: 768px) {
    &__head {
      flex-wrap: wrap;
    }

    &__marks {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: flex-start;
      flex-basis: 100%;
    }

    &__offer-line,
    &__offer-actions {
      flex-direction: column;
      align-items: flex-start;
    }
  }
}
</style>
